/**
 * Generates HTML email template for flight status notifications
 * Matches the TrustJet client-facing email design
 * Uses Remix Icon SVGs rendered as inline data-URI images
 */

const API_BASE_URL = "https://api.trustjet.ai:8000";

const formatImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_BASE_URL}${path}`;
};

const formatPriceForEmail = (price) => {
  if (price == null || isNaN(price)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const formatDateForEmail = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const formatTimeForEmail = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

// Remix Icon SVG paths (viewBox 0 0 24 24, fill)
const RI_PATHS = {
  plane: 'M14 8.94737L22 14V16L14 13.4737V18.8333L17 20.5V22L12.5 21L8 22V20.5L11 18.8333V13.4737L3 16V14L11 8.94737V3.5C11 2.67157 11.6716 2 12.5 2C13.3284 2 14 2.67157 14 3.5V8.94737Z',
  mapPin: 'M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z',
  building: 'M21 19H23V21H1V19H3V4C3 3.44772 3.44772 3 4 3H14C14.5523 3 15 3.44772 15 4V19H19V11H17V9H20C20.5523 9 21 9.44772 21 10V19ZM5 5V19H13V5H5ZM7 11H11V13H7V11ZM7 7H11V9H7V7Z',
  priceTag: 'M10.9042 2.10025L20.8037 3.51446L22.2179 13.414L13.0255 22.6063C12.635 22.9969 12.0019 22.9969 11.6113 22.6063L1.71184 12.7069C1.32131 12.3163 1.32131 11.6832 1.71184 11.2926L10.9042 2.10025ZM11.6113 4.22157L3.83316 11.9997L12.3184 20.485L20.0966 12.7069L19.036 5.28223L11.6113 4.22157ZM13.7327 10.5855C12.9516 9.80448 12.9516 8.53815 13.7327 7.7571C14.5137 6.97606 15.78 6.97606 16.5611 7.7571C17.3421 8.53815 17.3421 9.80448 16.5611 10.5855C15.78 11.3666 14.5137 11.3666 13.7327 10.5855Z',
  trophy: 'M13.0049 16.9409V19.0027H18.0049V21.0027H6.00488V19.0027H11.0049V16.9409C7.05857 16.4488 4.00488 13.0824 4.00488 9.00275V3.00275H20.0049V9.00275C20.0049 13.0824 16.9512 16.4488 13.0049 16.9409ZM6.00488 5.00275V9.00275C6.00488 12.3165 8.69117 15.0027 12.0049 15.0027C15.3186 15.0027 18.0049 12.3165 18.0049 9.00275V5.00275H6.00488ZM1.00488 5.00275H3.00488V9.00275H1.00488V5.00275ZM21.0049 5.00275H23.0049V9.00275H21.0049V5.00275Z',
  medal: 'M12 6.99999C16.4183 6.99999 20 10.5817 20 15C20 19.4183 16.4183 23 12 23C7.58172 23 4 19.4183 4 15C4 10.5817 7.58172 6.99999 12 6.99999ZM12 8.99999C8.68629 8.99999 6 11.6863 6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 11.6863 15.3137 8.99999 12 8.99999ZM12 10.5L13.3225 13.1797L16.2798 13.6094L14.1399 15.6953L14.645 18.6406L12 17.25L9.35497 18.6406L9.86012 15.6953L7.72025 13.6094L10.6775 13.1797L12 10.5ZM18 1.99999V4.99999L16.6366 6.13755C15.5305 5.5577 14.3025 5.17884 13.0011 5.04948L13 1.99899L18 1.99999ZM11 1.99899L10.9997 5.04939C9.6984 5.17863 8.47046 5.55735 7.36441 6.13703L6 4.99999V1.99999L11 1.99899Z',
  check: 'M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z',
  calendar: 'M9 1V3H15V1H17V3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9ZM20 11H4V19H20V11ZM8 13V15H6V13H8ZM13 13V15H11V13H13ZM18 13V15H16V13H18ZM7 5H4V9H20V5H17V7H15V5H9V7H7V5Z',
  time: 'M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17V14H11V7H13V12Z',
  flightTakeoff: 'M21.949 10.1118C22.1634 10.912 21.6886 11.7345 20.8884 11.9489L5.2218 16.1467C4.77856 16.2655 4.31138 16.0674 4.08866 15.6662L1.46582 10.9415L2.91471 10.5533L5.3825 12.9979L10.4778 11.6326L5.96728 4.55896L7.89913 4.04132L14.8505 10.4609L20.1119 9.05113C20.9121 8.83671 21.7346 9.31159 21.949 10.1118ZM4.00013 19H20.0001V21H4.00013V19Z',
  flightLand: 'M20.949 14.8882C20.7346 15.6884 19.9121 16.1633 19.1119 15.9489L3.44528 11.751C3.00205 11.6323 2.69653 11.2271 2.70424 10.7683L2.79514 5.36515L4.24403 5.75338L5.15891 9.10435L10.2542 10.4696L9.88479 2.08844L11.8166 2.60608L14.6269 11.6413L19.8883 13.0511C20.6885 13.2655 21.1634 14.088 20.949 14.8882ZM4.00008 19H20.0001V21H4.00008V19Z',
  arrowRight: 'M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z',
};

/**
 * Generate an inline <img> tag for a Remix Icon SVG
 * Uses data URI for maximum email client compatibility
 */
const icon = (name, size = 16, color = '#6B7280') => {
  const path = RI_PATHS[name];
  if (!path) return '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" fill="${color}"><path d="${path}"/></svg>`;
  const encoded = svg
    .replace(/"/g, "'")
    .replace(/#/g, '%23')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E')
    .replace(/\s+/g, ' ');
  return `<img src="data:image/svg+xml,${encoded}" width="${size}" height="${size}" alt="" style="display:inline-block;vertical-align:middle;" />`;
};

/**
 * Build the HTML email body for flight tracking notification
 * @param {Object} params
 * @param {Object} params.quote - Quote data
 * @param {Array} params.trackingUrl - { flightId, route, url }
 * @param {Array} params.flights - Flight data array
 * @returns {string} HTML email body
 */
/**
 * Render a single aircraft card HTML block for a bid option
 */
const buildAircraftCardHtml = (option) => {
  const aircraft = option?.aircraft;
  const operator = option?.operator;

  const imageUrl = aircraft?.image_urls?.length > 0
    ? formatImageUrl(aircraft.image_urls[0])
    : '';

  const modelName = (() => {
    if (!aircraft) return '';
    if (aircraft.aircraft_make_model) {
      const { manufacturer } = aircraft.aircraft_make_model;
      const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
      if (manufacturer && makeModel) {
        const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
        return `${manufacturer} ${modelPart}`.trim();
      }
      return makeModel || '';
    }
    return aircraft.make_model || aircraft.tail_number || '';
  })();

  const operatorName = operator?.name || '';
  const operatorLocation = [operator?.city, operator?.state].filter(Boolean).join(', ');
  const operatorDisplay = operatorLocation ? `${operatorName} • ${operatorLocation}` : operatorName;

  const price = formatPriceForEmail(option?.final_price);

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), color: '#374151', bg: '#F3F4F6', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', color: '#374151', bg: '#F3F4F6', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', color: '#15803D', bg: '#DCFCE7', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.length > 0
    ? amenities.map(a =>
        `<span style="display:inline-block;padding:4px 12px;background:#F3F4F6;color:#374151;font-size:12px;font-weight: 500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
      ).join('')
    : '';

  return `
          <tr>
            <td style="padding:24px 0px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #E5E7EB;border-radius:0px;overflow:hidden;">
                ${imageUrl ? `
                <tr>
                  <td>
                    <img src="${imageUrl}" alt="${modelName}" width="100%" style="display:block;width:100%;height:auto;object-fit:cover;max-height:280px;" />
                  </td>
                </tr>` : ''}
                <tr>
                  <td style="padding:20px;background-color: #FFFFFF;">
                    <h2 style="margin:0 0 4px;font-size:20px;font-weight:700;color:#111827;">${aircraft?.tail_number || ''}</h2>
                    <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>
                    ${operatorDisplay ? `<p style="margin:0 0 12px;font-size:13px;color:#6B7280;">${icon('building', 14, '#6B7280')} ${operatorDisplay}</p>` : ''}

                    <!-- Price -->
                    <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                      <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
                    </div>

                    <!-- Badges -->
                    ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

                    <!-- About -->
                    ${aircraft?.description ? `
                    <div style="margin-top:16px;">
                      <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                      <p style="margin:0;font-size:13px;color:#6B7280;line-height:1.5;">${aircraft.description}</p>
                    </div>` : ''}

                    <!-- Amenities -->
                    ${amenitiesHtml ? `
                    <div style="margin-top:16px;">
                      <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                      <div>${amenitiesHtml}</div>
                    </div>` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
};

export const buildFlightEmailHtml = ({ quote, trackingUrl, flights }) => {
  // Get all shortlisted bid options
  const shortlistedOptions = quote?.bid_items
    ?.flatMap(bi => bi.bid?.bid_options || [])
    ?.filter(opt => opt.shortlisted) || [];

  // Build aircraft cards for all shortlisted options
  const aircraftCardsHtml = shortlistedOptions.map(option => buildAircraftCardHtml(option)).join('');

  // Tracking URL
  const primaryTrackingUrl = trackingUrl.url || '#';

  // Leg details
  const legRows = (flights || []).map(f => `
    <tr>
      <td style="padding:12px 16px;border-bottom:1px solid #E5E7EB;">
        <span style="font-size:14px;font-weight:600;">${icon('flightTakeoff', 14, '#374151')} ${f.departure_airport || '—'}</span>
        <span style="margin:0 8px;vertical-align:middle;">${icon('arrowRight', 14, '#9CA3AF')}</span>
        <span style="font-size:14px;font-weight:600;">${icon('flightLand', 14, '#374151')} ${f.arrival_airport || '—'}</span>
      </td>
      <td style="padding:12px 16px;border-bottom:1px solid #E5E7EB;text-align:right;">
        <span style="font-size:13px;color:#6B7280;">${icon('calendar', 13, '#6B7280')} ${formatDateForEmail(f.departure_date || f.departure_time)}</span>
        <span style="font-size:13px;color:#6B7280;margin-left:12px;">${icon('time', 13, '#6B7280')} ${formatTimeForEmail(f.departure_date || f.departure_time)}</span>
      </td>
    </tr>
  `).join('');

  // Client name
  const clientName = [quote?.client?.first_name, quote?.client?.last_name].filter(Boolean).join(' ') || 'Valued Client';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Track Your Flight</title>
</head>
<body style="margin:0;padding:0;background-color:#F3F4F6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#F3F4F6;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background-color:#FFFFFF;border-radius:0px;overflow:hidden;">

          <!-- Track Flight Header -->
          <tr>
            <td align="center" style="padding:40px 24px 32px;background-color: #FFFFFF;">
              <div style="width:64px;height:64px;background-color:#DCFCE7;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;margin-bottom:16px;">
                ${icon('plane', 28, '#16A34A')}
              </div>
              <h3 style="margin:0 0 8px;font-size:17px;font-weight:400;color:#111827;">Track my current flight!</h3>
              <h3 style="margin:0 0 24px;font-size:17px;font-weight:400;color:#111827;">See its route and expected arrival time.</h3>
              <a href="${primaryTrackingUrl}" style="display:inline-block;padding:12px 32px;background-color:#0C3E71;color:#FFFFFF;text-decoration:none;font-size:14px;font-weight:400;border-radius:24px;">Track my flight</a>
            </td>
          </tr>

          <!-- Divider -->
          <tr><td style="padding:0 24px;"><hr style="border:none;border-top:1px solid #E5E7EB;margin:0;"></td></tr>

          <!-- Aircraft Cards -->
          ${aircraftCardsHtml}

          <!-- Leg Details -->
          ${legRows ? `
          <tr>
            <td style="padding: 0px;">
              <h2 style="margin:0 0 12px;font-size:18px;font-weight:700;color:#111827;">Leg Details</h2>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-top:1px solid #E5E7EB;overflow:hidden;">
                ${legRows}
              </table>
            </td>
          </tr>` : ''}

          <!-- Spacer -->
          <tr><td style="height:30px;"></td></tr>

          <!-- Footer -->
          <tr>
            <td style="padding:24px 0px;border-top:2px solid #E5E7EB;">
              <p style="margin:0 0 16px;font-size:12px;color:#9CA3AF;line-height:1.5;">
                Please do not reply to this e-mail, as it cannot be answered from this address.
              </p>
              <p style="margin:0 0 16px;font-size:12px;color:#6B7280;line-height:1.5;">
                For changes (which may result in a fare change) or questions about your reservation,
                you may contact <a href="https://gotrustjet.com" style="color:#1D4ED8;text-decoration:underline;">gotrustjet.com</a>.
                Support via telephone at 1-800-123-4567 in the U.S. and Canada or in the United Kingdom at 0800-123-4567.
                Elsewhere, please contact TrustJet.
              </p>
              <p style="margin:0 0 24px;font-size:12px;color:#6B7280;">
                Thank you for using <a href="https://gotrustjet.com" style="color:#1D4ED8;text-decoration:underline;">gotrustjet.com</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};
